import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import "./SourcePanel.css";
import { APP_VERSION_LABEL } from "../../config/appVersion";
import { toPersianDigits } from "../../utils/toPersianDigits";
import RetryLogoImage from "../media/RetryLogoImage";

const LANDING_PAGE_URL =
  process.env.REACT_APP_LANDING_PAGE_URL || "https://chekhabare.com";

const normalizeSourceSearchText = (value) => {
  return toPersianDigits(String(value || "").toLowerCase())
    .replace(/آ/g, "ا")
    .replace(/[\s\u200c\u200d\u2060]+/g, "");
};

const SourcePanel = ({
  sources = [],
  activeSources = [],
  onToggle,
  favicons = {},
  searchTerm = "",
  onSearchChange,
  isMobileOpen = false,
  onMobileClose,
  canMobileClose = true,
}) => {
  const [isMobileSearchExpanded, setIsMobileSearchExpanded] = useState(false);
  const [mobileKeyboardHeight, setMobileKeyboardHeight] = useState(0);
  const sourcePanelRef = useRef(null);
  const sourceListRef = useRef(null);
  const mobileSearchInputRef = useRef(null);
  const mobileLayoutViewportHeightRef = useRef(0);
  const isMobileDoneDisabled = !isMobileSearchExpanded && !canMobileClose;

  const scrollSourceListToTop = useCallback(() => {
    const sourceList = sourceListRef.current;

    if (!sourceList) {
      return;
    }

    try {
      sourceList.scrollTo({
        top: 0,
        behavior: "smooth",
      });
    } catch {
      sourceList.scrollTop = 0;
    }
  }, []);

  const collapseMobileSearch = useCallback(() => {
    onSearchChange?.("");

    if (
      typeof document !== "undefined" &&
      document.activeElement instanceof HTMLElement
    ) {
      document.activeElement.blur();
    }

    setIsMobileSearchExpanded(false);
  }, [onSearchChange]);

  useEffect(() => {
    if (!isMobileOpen) {
      setIsMobileSearchExpanded(false);
    }
  }, [isMobileOpen]);

  useEffect(() => {
    if (isMobileSearchExpanded) {
      scrollSourceListToTop();
    }
  }, [isMobileSearchExpanded, scrollSourceListToTop]);

  useEffect(() => {
    if (
      !isMobileOpen ||
      isMobileSearchExpanded ||
      typeof window === "undefined"
    ) {
      return undefined;
    }

    const captureMobileLayoutViewportHeight = () => {
      const documentHeight =
        typeof document !== "undefined"
          ? document.documentElement?.clientHeight || 0
          : 0;
      const visualViewportHeight =
        window.visualViewport?.height || 0;
      const visualViewportOffsetTop =
        window.visualViewport?.offsetTop || 0;

      mobileLayoutViewportHeightRef.current = Math.max(
        window.innerHeight || 0,
        documentHeight,
        visualViewportHeight + visualViewportOffsetTop
      );

      setMobileKeyboardHeight(0);
    };

    captureMobileLayoutViewportHeight();

    window.addEventListener(
      "resize",
      captureMobileLayoutViewportHeight
    );
    window.addEventListener(
      "orientationchange",
      captureMobileLayoutViewportHeight
    );

    return () => {
      window.removeEventListener(
        "resize",
        captureMobileLayoutViewportHeight
      );
      window.removeEventListener(
        "orientationchange",
        captureMobileLayoutViewportHeight
      );
    };
  }, [isMobileOpen, isMobileSearchExpanded]);

  useEffect(() => {
    if (
      !isMobileOpen ||
      !isMobileSearchExpanded ||
      typeof window === "undefined"
    ) {
      setMobileKeyboardHeight(0);
      return undefined;
    }

    const visualViewport = window.visualViewport;
    let animationFrameId = 0;

    const updateMobileKeyboardHeight = () => {
      if (animationFrameId) {
        window.cancelAnimationFrame(animationFrameId);
      }

      animationFrameId = window.requestAnimationFrame(() => {
        const documentHeight =
          typeof document !== "undefined"
            ? document.documentElement?.clientHeight || 0
            : 0;
        const visibleViewportHeight = visualViewport
          ? visualViewport.height + visualViewport.offsetTop
          : window.innerHeight || 0;
        const layoutViewportHeight = Math.max(
          mobileLayoutViewportHeightRef.current,
          window.innerHeight || 0,
          documentHeight
        );
        const measuredKeyboardHeight = Math.max(
          0,
          Math.round(layoutViewportHeight - visibleViewportHeight)
        );
        const nextKeyboardHeight =
          measuredKeyboardHeight >= 80
            ? measuredKeyboardHeight
            : 0;

        setMobileKeyboardHeight((currentHeight) => {
          return currentHeight === nextKeyboardHeight
            ? currentHeight
            : nextKeyboardHeight;
        });
      });
    };

    updateMobileKeyboardHeight();

    window.addEventListener("resize", updateMobileKeyboardHeight);
    window.addEventListener(
      "orientationchange",
      updateMobileKeyboardHeight
    );
    visualViewport?.addEventListener(
      "resize",
      updateMobileKeyboardHeight
    );
    visualViewport?.addEventListener(
      "scroll",
      updateMobileKeyboardHeight
    );

    return () => {
      if (animationFrameId) {
        window.cancelAnimationFrame(animationFrameId);
      }

      window.removeEventListener(
        "resize",
        updateMobileKeyboardHeight
      );
      window.removeEventListener(
        "orientationchange",
        updateMobileKeyboardHeight
      );
      visualViewport?.removeEventListener(
        "resize",
        updateMobileKeyboardHeight
      );
      visualViewport?.removeEventListener(
        "scroll",
        updateMobileKeyboardHeight
      );
    };
  }, [isMobileOpen, isMobileSearchExpanded]);

  useEffect(() => {
    if (
      !isMobileOpen ||
      !isMobileSearchExpanded ||
      typeof document === "undefined"
    ) {
      return undefined;
    }

    const handlePointerDownOutside = (event) => {
      const panel = sourcePanelRef.current;

      if (!panel) {
        return;
      }

      const eventPath =
        typeof event.composedPath === "function"
          ? event.composedPath()
          : [];

      const isInsidePanel =
        panel.contains(event.target) || eventPath.includes(panel);

      if (isInsidePanel) {
        return;
      }

      collapseMobileSearch();
    };

    document.addEventListener(
      "pointerdown",
      handlePointerDownOutside,
      true
    );

    return () => {
      document.removeEventListener(
        "pointerdown",
        handlePointerDownOutside,
        true
      );
    };
  }, [collapseMobileSearch, isMobileOpen, isMobileSearchExpanded]);

  const filteredSources = useMemo(() => {
    const normalizedSearchTerm = normalizeSourceSearchText(searchTerm);

    if (!normalizedSearchTerm) return sources;

    return sources.filter((source) => {
      const normalizedSource = normalizeSourceSearchText(source);
      return normalizedSource.includes(normalizedSearchTerm);
    });
  }, [sources, searchTerm]);

  const groupedSources = useMemo(() => {
    const active = [];
    const inactive = [];

    filteredSources.forEach((source) => {
      if (activeSources.includes(source)) {
        active.push(source);
      } else {
        inactive.push(source);
      }
    });

    return { active, inactive };
  }, [activeSources, filteredSources]);

  const keepMobileSearchFocused = () => {
    if (!isMobileSearchExpanded) {
      return;
    }

    const input = mobileSearchInputRef.current;

    if (!input) {
      return;
    }

    try {
      input.focus({ preventScroll: true });
    } catch {
      input.focus();
    }
  };

  const renderSourceItem = (source) => {
    const isActive = activeSources.includes(source);
    const faviconUrl = favicons[source];
    const sourceDisplay = toPersianDigits(source);

    return (
      <button
        key={source}
        type="button"
        className={`source-panel__item ${
          isActive ? "source-panel__item--active" : ""
        }`}
        onPointerDown={(event) => {
          if (isMobileSearchExpanded) {
            event.preventDefault();
          }
        }}
        onClick={() => {
          onToggle?.(source);
          keepMobileSearchFocused();
        }}
        title={sourceDisplay}
        aria-label={sourceDisplay}
        aria-pressed={isActive}
      >
        <div className="source-panel__logo">
          {faviconUrl ? (
            <RetryLogoImage
              src={faviconUrl}
              alt=""
              className="source-panel__icon"
              fallbackText={source.charAt(0)}
              loading="lazy"
              decoding="async"
            />
          ) : (
            <span className="source-panel__default-icon" aria-hidden="true">
              {source.charAt(0)}
            </span>
          )}

          <span
            className={`source-panel__indicator ${
              isActive ? "source-panel__indicator--active" : ""
            }`}
          >
            <svg
              className="ui-svg-icon source-panel__indicator-icon"
              viewBox="0 0 24 24"
              aria-hidden="true"
              focusable="false"
            >
              {isActive ? (
                <line x1="5" y1="12" x2="19" y2="12" />
              ) : (
                <>
                  <line x1="12" y1="5" x2="12" y2="19" />
                  <line x1="5" y1="12" x2="19" y2="12" />
                </>
              )}
            </svg>
          </span>
        </div>

        <span className="source-panel__name">{sourceDisplay}</span>
      </button>
    );
  };

  return (
    <div
      ref={sourcePanelRef}
      className={`source-panel${
        isMobileOpen ? " source-panel--mobile-open" : ""
      }${
        isMobileSearchExpanded
          ? " source-panel--mobile-search-expanded"
          : ""
      }`}
      style={{
        "--source-panel-mobile-keyboard-height":
          `${mobileKeyboardHeight}px`,
      }}
    >
      <div className="source-panel__search desktop-only">
        <div className="mobile-search-capsule">
          <input
            type="text"
            className="mobile-search-input"
            placeholder="جستجوی منبع..."
            value={searchTerm}
            onPointerDown={scrollSourceListToTop}
            onFocus={scrollSourceListToTop}
            onChange={(event) => onSearchChange?.(event.target.value)}
          />
        </div>
      </div>

      <div
        ref={sourceListRef}
        className="source-panel__list"
      >
        {filteredSources.length === 0 && (
          <div className="source-panel__empty">منبعی یافت نشد</div>
        )}

        {groupedSources.active.map(renderSourceItem)}

        {groupedSources.active.length > 0 &&
          groupedSources.inactive.length > 0 && (
            <div
              className="source-panel__group-divider"
              aria-hidden="true"
            />
          )}

        {groupedSources.inactive.map(renderSourceItem)}

        <div
          className="source-panel__meta"
          aria-label="اطلاعات چه‌خبره"
        >
          <a
            className="source-panel__about-link"
            href={LANDING_PAGE_URL}
            target="_blank"
            rel="noopener noreferrer"
          >
            درباره چه‌خبره
          </a>

          <span
            className="source-panel__meta-separator"
            aria-hidden="true"
          >
            ·
          </span>

          <span
            className="source-panel__version"
            dir="ltr"
          >
            {APP_VERSION_LABEL}
          </span>
        </div>
      </div>

      {isMobileOpen && (
        <div className="source-panel__mobile-bottom-container">
          <div
            className={`mobile-search-capsule ${
              isMobileSearchExpanded
                ? "mobile-search-capsule--expanded"
                : "mobile-search-capsule--collapsed"
            }`}
          >
            {isMobileSearchExpanded ? (
              <input
                type="search"
                className="mobile-search-input"
                placeholder="جستجوی منبع..."
                ref={mobileSearchInputRef}
                value={searchTerm}
                onPointerDown={scrollSourceListToTop}
                onFocus={scrollSourceListToTop}
                onChange={(event) => onSearchChange?.(event.target.value)}
                autoCorrect="off"
                autoCapitalize="none"
                spellCheck={false}
                enterKeyHint="search"
                autoFocus
              />
            ) : (
              <button
                type="button"
                className="mobile-search-trigger"
                onClick={() => setIsMobileSearchExpanded(true)}
                title="جستجوی منبع"
                aria-label="جستجوی منبع"
              >
                <svg
                  className="ui-svg-icon"
                  viewBox="0 0 24 24"
                  aria-hidden="true"
                  focusable="false"
                >
                  <circle cx="11" cy="11" r="7" />
                  <line x1="16.5" y1="16.5" x2="21" y2="21" />
                </svg>
              </button>
            )}

            <button
              type="button"
              className={`mobile-close-circle ${
                isMobileSearchExpanded
                  ? "mobile-close-circle--keyboard"
                  : ""
              }${
                isMobileDoneDisabled
                  ? " mobile-close-circle--disabled"
                  : ""
              }`}
              onPointerDown={(event) => {
                if (isMobileSearchExpanded) {
                  event.preventDefault();
                }
              }}
              onClick={
                isMobileSearchExpanded
                  ? collapseMobileSearch
                  : canMobileClose
                  ? onMobileClose
                  : undefined
              }
              disabled={isMobileDoneDisabled}
              title={
                isMobileSearchExpanded
                  ? "خروج از جستجو"
                  : canMobileClose
                  ? "بستن پنل"
                  : "حداقل یک منبع انتخاب کنید"
              }
              aria-label={
                isMobileSearchExpanded
                  ? "خروج از جستجو"
                  : canMobileClose
                  ? "بستن پنل"
                  : "حداقل یک منبع انتخاب کنید"
              }
            >
              <svg
                className="ui-svg-icon"
                viewBox="0 0 24 24"
                aria-hidden="true"
                focusable="false"
              >
                {isMobileSearchExpanded ? (
                  <polyline points="15 18 9 12 15 6" />
                ) : (
                  <polyline points="20 6 9 17 4 12" />
                )}
              </svg>
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default SourcePanel;

